import { Link } from 'react-router-dom';
import { PersonChips } from './PeopleChips';
import { PLAN_LABEL, STAGE_LABEL, asksLine, attemptsLine, sinceLastEvent, stuckLine, stuckWords, type PersonRow } from '@/lib/people-data';
import { NOT_MEASURED, num } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * The People table on a phone: one person per card. The name, where they
 * stopped, how long since the last event, the counts that matter, and the
 * action: Re-engage opens the person's page with the task panel already
 * open. Same rows as the table, no extra read; a value the server could
 * not measure prints the words for it. A person short of a first check
 * also carries why (the stuck reason the server read) and how many
 * attempts the client reported. The chips under the name are the counts of
 * what they wrote us and told us; a chip at zero is not drawn.
 */
export function PeopleCards({ people, now }: { people: PersonRow[]; now?: number }) {
  if (people.length === 0) return <div className={s.empty}>Nobody matches these filters.</div>;
  return (
    <div className={s.people} data-panel="people-cards">
      {people.map((p) => {
        const asks = asksLine(p);
        return (
          <article key={p.userId} className={s.pCard} data-user={p.userId}>
            <div className={s.pCardTop}>
              <Link to={`/people/${p.userId}`} className={s.rowLink}>{p.name || p.email}</Link>
              <span className={`${s.pill} ${p.stage === 'paid' ? s.pill_paid : s.pill_stage}`}>{STAGE_LABEL[p.stage]}</span>
            </div>
            <div className={s.sub}>{p.email}{p.phone ? ` · ${p.phone}` : ''}{p.qa ? ' · QA account' : ''}{!p.verified ? ' · not verified' : ''}</div>
            <PersonChips p={p} />
            <div className={s.pCardStuck}>{stuckLine(p)}</div>
            <StuckCardLine p={p} />
            <div className={s.pCardSince}>last event <strong>{sinceLastEvent(p, now)}</strong>{p.lastSeenSource ? ` · ${p.lastSeenSource.replace(/_/g, ' ')}` : ''}</div>
            <dl className={s.pCardNums}>
              <div className={s.pCardNum}><dt>Plan</dt><dd>{PLAN_LABEL[p.plan]}</dd></div>
              <div className={s.pCardNum}><dt>Checks</dt><dd>{num(p.checks)}{p.streak !== null ? ` · streak ${num(p.streak)}` : ''}</dd></div>
              <div className={s.pCardNum}><dt>Active Mode 7 d</dt><dd>{p.activeMinutes7d === null ? <span className={s.missing}>{NOT_MEASURED}</span> : `${num(p.activeMinutes7d)} min`}</dd></div>
              <div className={s.pCardNum}><dt>Asks</dt><dd>{asks ? <span className={s.usageAsk}>{asks}</span> : 'none open'}</dd></div>
            </dl>
            <div className={s.pCardActions}>
              <Link className={s.btn} to={`/people/${p.userId}?reengage=1`}>Re-engage</Link>
              {p.ghlUrl ? <a className={s.btnGhost} href={p.ghlUrl} target="_blank" rel="noreferrer">GHL</a> : <span className={s.missing}>no GHL contact</span>}
            </div>
          </article>
        );
      })}
    </div>
  );
}

/** "Why: camera gate never passed · 2 attempts, 1 failed", only for a person short of a first check. */
function StuckCardLine({ p }: { p: PersonRow }) {
  const why = stuckWords(p);
  const tries = attemptsLine(p.attempts);
  if (!why && !tries) return null;
  return (
    <div className={s.pCardSince} data-stuck={why ? p.stuckReason ?? 'none' : undefined}>
      {why ? <>why <strong className={why.measured ? '' : s.missing}>{why.text}</strong></> : null}
      {why && tries ? ' · ' : ''}
      {tries}
    </div>
  );
}
